"use client";

import * as React from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";

// Page sizes offered by the rows-per-page picker, smallest first.
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Only offer a size once the list is long enough to fill the one below it, so
// a 12-row list does not invite the user to ask for 100 at a time.
export function usablePageSizeOptions(options, rowCount) {
  return options.filter((option, idx) => idx === 0 || rowCount > options[idx - 1]);
}

// Disabled controls stay visible rather than inert: reduced opacity, a
// not-allowed cursor and no hover response, so page 1 reads as page 1.
const pagerButtonClasses = cn(
  "h-8 w-8 border-moblyze-grey-border-dark bg-moblyze-grey-dark p-0 text-white",
  "transition-colors hover:border-moblyze-grey-border-dark hover:bg-moblyze-charcoal-light hover:text-white",
  "focus-visible:ring-1 focus-visible:ring-moblyze-green focus-visible:ring-offset-0",
  "disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-40",
  "disabled:hover:bg-moblyze-grey-dark"
);

function PagerButton({ label, icon: IconComponent, onClick, disabled }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={pagerButtonClasses}
    >
      <IconComponent aria-hidden="true" />
    </Button>
  );
}

/**
 * Pager for a TanStack table: which records are on screen, how many rows to
 * show at a time, and the first/previous/next/last controls.
 *
 * It only reads the table instance, so it works the same whether the caller
 * pages on the server (`manualPagination` plus a `rowCount`) or in the
 * browser with `getPaginationRowModel`.
 *
 * `loading` means a page is already in flight. Every control that would start
 * another one is disabled for the duration, so an impatient double-click on
 * "next" cannot queue two page changes; it uses the same disabled treatment as
 * a control that is off the end of the list, so there is one way for a pager
 * control to read as unavailable rather than two.
 */
export function DataTablePagination({
  table,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className,
  loading = false,
}) {
  const { pageIndex, pageSize } = table.getState().pagination;
  const rowCount = table.getRowCount();
  const pageCount = table.getPageCount();
  const rowsOnPage = table.getRowModel().rows.length;

  const firstRow = rowsOnPage === 0 ? 0 : pageIndex * pageSize + 1;
  const lastRow = pageIndex * pageSize + rowsOnPage;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-4 py-3 text-[13px] text-moblyze-grey-light",
        className
      )}
    >
      <span className="whitespace-nowrap">
        Showing{" "}
        <span className="font-medium text-white">
          {firstRow.toLocaleString()}-{lastRow.toLocaleString()}
        </span>{" "}
        of{" "}
        <span className="font-medium text-white">
          {rowCount.toLocaleString()}
        </span>
      </span>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2">
          <span className="whitespace-nowrap">Rows per page</span>
          <select
            value={pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            disabled={loading}
            className="h-8 rounded-md border border-moblyze-grey-border-dark bg-moblyze-grey-dark px-2 text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-moblyze-green disabled:cursor-not-allowed disabled:opacity-40"
          >
            {usablePageSizeOptions(pageSizeOptions, rowCount).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </label>

        <span className="whitespace-nowrap">
          Page{" "}
          <span className="font-medium text-white">
            {(pageIndex + 1).toLocaleString()}
          </span>{" "}
          of{" "}
          <span className="font-medium text-white">
            {Math.max(pageCount, 1).toLocaleString()}
          </span>
        </span>

        <div className="flex items-center gap-1">
          <PagerButton
            label="Go to first page"
            icon={ChevronsLeft}
            onClick={() => table.firstPage()}
            disabled={loading || !table.getCanPreviousPage()}
          />
          <PagerButton
            label="Go to previous page"
            icon={ChevronLeft}
            onClick={() => table.previousPage()}
            disabled={loading || !table.getCanPreviousPage()}
          />
          <PagerButton
            label="Go to next page"
            icon={ChevronRight}
            onClick={() => table.nextPage()}
            disabled={loading || !table.getCanNextPage()}
          />
          <PagerButton
            label="Go to last page"
            icon={ChevronsRight}
            onClick={() => table.lastPage()}
            disabled={loading || !table.getCanNextPage()}
          />
        </div>
      </div>
    </div>
  );
}

export default DataTablePagination;
